/**
 * modelSchema — 저장된 모델 데이터의 스키마 버전 관리
 *
 * 왜 필요한가
 * -----------
 * 다이어그램 내용(클라우드 `dgrmCn`, 내보낸 JSON 파일, 세션 복원 기록)에는 그동안 **버전 표시가
 * 없었다**. 빌드가 올라가며 필드가 늘어나도, 읽는 쪽에서 `edges || []` 처럼 기본값을 채워
 * 버티는 방식이 유일한 대응이었다(2026-09-07 점검). 개발 단계에서는 그것으로 충분했지만,
 * 사용자가 내보낸 파일과 클라우드에 쌓인 모델은 손을 떠난 데이터라 규칙이 필요하다.
 *
 * 규칙
 * ----
 * 1. 저장할 때 본문에 `schemaVersion` 을 찍는다.
 * 2. 읽을 때 세 갈래로 나눈다.
 *    - 같음   → 그대로 읽는다
 *    - 낮음   → 변환기를 순서대로 적용해 최신으로 올린다
 *    - 높음   → **읽지 않고 막는다**. 데이터를 이해하지 못하는 상태에서 열면 모르는 필드를
 *               떨어뜨린 채 저장해 원본을 훼손한다. 옛 빌드(데스크톱 설치판)가 새 파일을 만나는
 *               경우가 실제로 잦다.
 * 3. 버전이 없는 데이터는 전부 `0` 으로 본다. 지금 저장돼 있는 것이 모두 여기 해당한다.
 *
 * 변환의 두 종류
 * -------------
 * - `lossless`: 모양만 보정한다(빠진 배열 채우기 등). 원본을 덮어써도 잃는 것이 없으므로
 *   그대로 열고 그대로 저장한다.
 * - `structural`: 데이터 구조가 실제로 바뀐다. 이 경우 호출자는 **복제본을 만들어 변환**하고
 *   원본은 그대로 두어야 한다(`requiresClone` 참고).
 *
 * 지금은 0 → 1 변환 하나뿐이고 `lossless` 다. 즉 동작은 이전과 같고, 뼈대만 놓인 상태다.
 */

import { MIGRATIONS } from './migrations';

/**
 * 현재 빌드가 쓰는 스키마 버전.
 *
 * **예전 빌드가 잘못 읽을 변경**을 했을 때만 올린다(이름 변경·의미 변경·필드 제거).
 * 필드를 더하기만 하는 변경은 올리지 않는다 — 예전 빌드는 모르는 키를 무시하므로
 * 호환이 깨지지 않고, 버전을 올리면 옛 빌드에서 못 열게 될 뿐이다.
 * 자세한 기준은 `migrations.js` 머리말 참고.
 */
export const CURRENT_SCHEMA_VERSION = 1;

/** 저장 본문에 찍히는 키 이름 */
export const SCHEMA_VERSION_KEY = 'schemaVersion';

/** 버전이 없는 옛 데이터의 버전 */
export const LEGACY_SCHEMA_VERSION = 0;

/** 읽기 판정 결과 */
export const SCHEMA_STATUS = {
  CURRENT: 'current',
  OLDER: 'older',
  NEWER: 'newer',
};

/**
 * 저장된 본문에서 스키마 버전을 읽는다. 없거나 숫자가 아니면 0.
 * @param {Object} payload
 * @returns {number}
 */
export const readSchemaVersion = (payload) => {
  const raw = payload && payload[SCHEMA_VERSION_KEY];
  const num = Number(raw);
  return Number.isFinite(num) && num >= 0 ? num : LEGACY_SCHEMA_VERSION;
};

/**
 * 현재 빌드가 이 데이터를 어떻게 다뤄야 하는지 판정한다.
 * @param {Object} payload
 * @returns {{status: string, version: number}}
 */
export const inspectSchema = (payload) => {
  const version = readSchemaVersion(payload);
  if (version > CURRENT_SCHEMA_VERSION) return { status: SCHEMA_STATUS.NEWER, version };
  if (version < CURRENT_SCHEMA_VERSION) return { status: SCHEMA_STATUS.OLDER, version };
  return { status: SCHEMA_STATUS.CURRENT, version };
};

/**
 * 현재 빌드보다 새로운 데이터를 열려 할 때 던지는 오류.
 * 호출자가 사용자에게 안내 문구를 띄울 수 있도록 버전을 담는다.
 */
export class SchemaTooNewError extends Error {
  constructor(version, label) {
    super(
      `${label ? `'${label}' 은(는) ` : ''}이 버전보다 새로운 형식입니다 ` +
      `(데이터 v${version}, 현재 프로그램 v${CURRENT_SCHEMA_VERSION}). ` +
      '최신 버전에서 열어 주세요.'
    );
    this.name = 'SchemaTooNewError';
    this.dataVersion = version;
    this.appVersion = CURRENT_SCHEMA_VERSION;
    this.modelLabel = label || null;
  }
}

/** 모델 본문에서 배열이어야 하는 필드 */
const ARRAY_FIELDS = ['nodes', 'edges', 'attrs', 'exclusiveGroups', 'subtypeGroups'];

/**
 * 읽어 들인 본문을 **항상 다룰 수 있는 모양**으로 맞춘다.
 *
 * 파일 가져오기 경로는 파일 내용을 그대로 펼쳐 넣어서, `edges` 가 없는 옛 파일을 열면
 * `undefined` 가 스토어에 들어가고 이후 리듀서가 `undefined.map` 으로 죽었다.
 * 변환 이전에 이 단계를 거치면 그 경로가 막힌다.
 *
 * @param {Object} payload
 * @returns {Object} 새 객체 (원본은 건드리지 않는다)
 */
export const normalizeModelPayload = (payload) => {
  const src = payload && typeof payload === 'object' ? payload : {};
  const out = { ...src };
  ARRAY_FIELDS.forEach((field) => {
    if (!Array.isArray(out[field])) out[field] = [];
  });
  if (!out.modelConfig || typeof out.modelConfig !== 'object') out.modelConfig = {};
  if (out.viewport === undefined) out.viewport = null;
  return out;
};

/** 적용해야 할 변환 목록 (버전 오름차순) */
const pendingMigrations = (fromVersion) =>
  MIGRATIONS
    .filter((m) => m.from >= fromVersion && m.to <= CURRENT_SCHEMA_VERSION)
    .sort((a, b) => a.from - b.from);

/**
 * 이 데이터를 최신으로 올릴 때 **복제본이 필요한지** 알려준다.
 * 구조를 바꾸는 변환이 하나라도 끼어 있으면 true.
 * @param {Object} payload
 * @returns {boolean}
 */
export const requiresClone = (payload) => {
  const { status, version } = inspectSchema(payload);
  if (status !== SCHEMA_STATUS.OLDER) return false;
  return pendingMigrations(version).some((m) => m.structural === true);
};

/**
 * 저장된 본문을 현재 스키마로 올린다.
 *
 * @param {Object} payload 저장돼 있던 본문
 * @param {{label?: string}} [options] label 은 오류 메시지에 쓰는 모델명
 * @returns {{model: Object, from: number, to: number, applied: string[], converted: boolean, needsClone: boolean}}
 * @throws {SchemaTooNewError} 현재 빌드보다 새로운 데이터일 때
 */
export const migrateModelPayload = (payload, options = {}) => {
  const { status, version } = inspectSchema(payload);

  if (status === SCHEMA_STATUS.NEWER) {
    throw new SchemaTooNewError(version, options.label);
  }

  const needsClone = requiresClone(payload);
  let model = normalizeModelPayload(payload);
  const applied = [];

  if (status === SCHEMA_STATUS.OLDER) {
    pendingMigrations(version).forEach((m) => {
      model = m.migrate(model);
      applied.push(`${m.from}→${m.to}`);
    });
  }

  model[SCHEMA_VERSION_KEY] = CURRENT_SCHEMA_VERSION;

  return {
    model,
    from: version,
    to: CURRENT_SCHEMA_VERSION,
    applied,
    converted: applied.length > 0,
    needsClone,
  };
};

/**
 * 저장 직전에 현재 스키마 버전을 찍는다.
 * @param {Object} payload
 * @returns {Object} 새 객체
 */
export const stampSchemaVersion = (payload) => ({
  ...(payload || {}),
  [SCHEMA_VERSION_KEY]: CURRENT_SCHEMA_VERSION,
});

/**
 * 세션 복원·실행취소 기록처럼 **버려도 되는** 임시 데이터용 판정.
 * 변환하지 않고, 현재 버전이 아니면 쓰지 않는다(옛 기록을 되살리는 편이 더 위험하다).
 * @param {Object} payload
 * @returns {boolean} 그대로 써도 되는지
 */
export const isDisposableRecordUsable = (payload) =>
  inspectSchema(payload).status === SCHEMA_STATUS.CURRENT;
